import { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import { Search, Filter, X, Check, LayoutGrid, List, Users, TrendingUp, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { BROADCAST_INVITE_BATCH_MAX } from '../lib/broadcastInviteLimits';

interface ManualKOLSelectorProps {
  /** Required for KOL search; if missing, the modal explains publish / agency assignment. */
  agencyOrgId?: string | null;
  organizationId?: string;
  onClose: () => void;
  onBroadcast: (kolIds: string[]) => void;
}

interface KOL {
  id: string;
  name: string;
  email: string;
  tags: { tag: { id: string; name: string } }[];
  platforms: any;
  audience: any;
  matchScore: number;
  totalFollowers: number;
  avgEngagement: number;
}

const RECENT_KEY = (org: string) => `kolSearchRecent_${org}`;

const PAGE_SIZE = 50;

export default function ManualKOLSelector({ agencyOrgId, organizationId, onClose, onBroadcast }: ManualKOLSelectorProps) {
  const [kols, setKols] = useState<KOL[]>([]);
  const [selectedKolIds, setSelectedKolIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [minFollowers, setMinFollowers] = useState<string>('');
  const [maxFollowers, setMaxFollowers] = useState<string>('');
  const [minEngagement, setMinEngagement] = useState<string>('');
  const [majorityRace, setMajorityRace] = useState<string>('');
  const [sex, setSex] = useState<string>('');
  const [sexThreshold, setSexThreshold] = useState<string>('60');
  const [ageGroup, setAgeGroup] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('matchScore');
  const [language, setLanguage] = useState('');
  const [ethnicityFilter, setEthnicityFilter] = useState('');
  const [minAvgPostRate, setMinAvgPostRate] = useState('');
  const [maxAvgPostRate, setMaxAvgPostRate] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableTo, setAvailableTo] = useState('');
  const [suggestions, setSuggestions] = useState<{ id: string; name: string; email?: string }[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [savedSearchName, setSavedSearchName] = useState('');
  const [tagFacet, setTagFacet] = useState<Record<string, number>>({});

  const [showFilters, setShowFilters] = useState(false);
  const [allTags, setAllTags] = useState<{ id: string; name: string; parentId?: string | null }[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const tagById = useMemo(() => new Map(allTags.map((t) => [t.id, t])), [allTags]);

  const getRootTagId = (tagId: string): string => {
    let cur = tagId;
    for (let i = 0; i < 50; i++) {
      const t = tagById.get(cur);
      if (!t?.parentId) return cur;
      cur = t.parentId;
    }
    return cur;
  };

  const rootTags = useMemo(
    () => allTags.filter((t) => !t.parentId).sort((a, b) => a.name.localeCompare(b.name)),
    [allTags]
  );

  /** When any search filter changes, reset to page 1 (useLayoutEffect runs before debounced fetch effect). */
  const filterSignature = useMemo(
    () =>
      JSON.stringify({
        agencyOrgId: agencyOrgId || '',
        searchTerm,
        selectedTags: [...selectedTags].sort(),
        selectedPlatforms: [...selectedPlatforms].sort(),
        minFollowers,
        maxFollowers,
        minEngagement,
        majorityRace,
        sex,
        sexThreshold,
        ageGroup,
        sortBy,
        language,
        ethnicityFilter,
        minAvgPostRate,
        maxAvgPostRate,
        availableFrom,
        availableTo,
      }),
    [
      agencyOrgId,
      searchTerm,
      selectedTags,
      selectedPlatforms,
      minFollowers,
      maxFollowers,
      minEngagement,
      majorityRace,
      sex,
      sexThreshold,
      ageGroup,
      sortBy,
      language,
      ethnicityFilter,
      minAvgPostRate,
      maxAvgPostRate,
      availableFrom,
      availableTo,
    ]
  );

  useLayoutEffect(() => {
    setPage(1);
  }, [filterSignature]);

  const childTagsOf = (pid: string) =>
    allTags.filter((t) => t.parentId === pid).sort((a, b) => a.name.localeCompare(b.name));

  const initialSearchDone = useRef(false);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    if (!agencyOrgId) return;
    if (!initialSearchDone.current) {
      initialSearchDone.current = true;
      void searchKOLs();
      return;
    }
    const timer = setTimeout(() => {
      void searchKOLs();
    }, 300);
    return () => clearTimeout(timer);
  }, [filterSignature, page, agencyOrgId]);

  useEffect(() => {
    if (!agencyOrgId) return;
    try {
      const r = localStorage.getItem(RECENT_KEY(agencyOrgId));
      if (r) setRecentSearches(JSON.parse(r));
    } catch {
      /* ignore */
    }
  }, [agencyOrgId]);

  useEffect(() => {
    if (!agencyOrgId || searchTerm.trim().length < 2) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/kols/suggestions', {
          params: { agencyOrgId, q: searchTerm.trim(), limit: 8 },
        });
        const next = res.data || [];
        setSuggestions(next);
        setSuggestionsOpen(next.length > 0);
      } catch {
        setSuggestions([]);
        setSuggestionsOpen(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [searchTerm, agencyOrgId]);

  useEffect(() => {
    if (!suggestionsOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!searchBoxRef.current?.contains(target)) {
        setSuggestionsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [suggestionsOpen]);

  const loadTags = async () => {
    try {
      const response = await api.get('/tags');
      setAllTags(response.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const searchKOLs = async () => {
    try {
      setLoading(true);
      
      if (!agencyOrgId) {
        console.error('[MANUAL SEARCH] Missing agencyOrgId!');
        setKols([]);
        setTotalCount(0);
        return;
      }

      const params: any = {
        agencyOrgId,
        sortBy,
      };

      if (searchTerm) params.search = searchTerm;
      if (selectedTags.length > 0) params.tags = selectedTags.join(',');
      if (selectedPlatforms.length > 0) params.platforms = selectedPlatforms.join(',');
      if (minFollowers) params.minFollowers = minFollowers;
      if (maxFollowers) params.maxFollowers = maxFollowers;
      if (minEngagement) params.minEngagement = minEngagement;
      if (majorityRace) params.majorityRace = majorityRace;
      if (sex) {
        params.sex = sex;
        params.sexThreshold = sexThreshold;
      }
      if (ageGroup) params.ageGroup = ageGroup;
      if (language.trim()) params.language = language.trim();
      if (ethnicityFilter.trim()) params.ethnicity = ethnicityFilter.trim();
      if (minAvgPostRate) params.minAvgPostRate = minAvgPostRate;
      if (maxAvgPostRate) params.maxAvgPostRate = maxAvgPostRate;
      if (availableFrom && availableTo) {
        params.availableFrom = availableFrom;
        params.availableTo = availableTo;
      }

      params.page = String(page);
      params.limit = String(PAGE_SIZE);

      console.log('[MANUAL SEARCH] Searching with params:', params);
      const response = await api.get('/kols/search/manual', { params });
      console.log('[MANUAL SEARCH] Found', response.data.total, 'KOLs');
      const list = response.data.kols || [];
      setKols(list);
      setTotalCount(response.data.total);
      const total = typeof response.data.total === 'number' ? response.data.total : list.length;
      const tp =
        total === 0 ? 1 : Math.max(1, Math.ceil(total / PAGE_SIZE));
      setTotalPages(typeof response.data.totalPages === 'number' ? Math.max(1, response.data.totalPages) : tp);
      const facet: Record<string, number> = {};
      list.forEach((kol: KOL) => {
        kol.tags?.forEach((t) => {
          const tid = t.tag?.id;
          if (!tid) return;
          const rootId = getRootTagId(tid);
          facet[rootId] = (facet[rootId] || 0) + 1;
        });
      });
      setTagFacet(facet);
    } catch (error: any) {
      console.error('[MANUAL SEARCH] Search failed:', error);
      console.error('[MANUAL SEARCH] Error response:', error.response?.data);
      setKols([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const toggleKOL = (kolId: string) => {
    const newSelected = new Set(selectedKolIds);
    if (newSelected.has(kolId)) {
      newSelected.delete(kolId);
    } else {
      if (newSelected.size >= BROADCAST_INVITE_BATCH_MAX) {
        alert(
          `You can invite up to ${BROADCAST_INVITE_BATCH_MAX} KOLs per send. Send this batch first, then invite more.`,
        );
        return;
      }
      newSelected.add(kolId);
    }
    setSelectedKolIds(newSelected);
  };

  const selectAll = () => {
    const next = new Set(selectedKolIds);
    for (const k of kols) {
      if (next.size >= BROADCAST_INVITE_BATCH_MAX) break;
      next.add(k.id);
    }
    setSelectedKolIds(next);
    const hitCap = next.size >= BROADCAST_INVITE_BATCH_MAX && kols.some((k) => !next.has(k.id));
    if (hitCap) {
      alert(
        `Only ${BROADCAST_INVITE_BATCH_MAX} KOLs can be invited per send. Send this batch first, then select more.`,
      );
    }
  };

  const clearSelection = () => {
    setSelectedKolIds(new Set());
  };

  const handleBroadcast = () => {
    onBroadcast(Array.from(selectedKolIds));
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
    setSelectedPlatforms([]);
    setMinFollowers('');
    setMaxFollowers('');
    setMinEngagement('');
    setMajorityRace('');
    setSex('');
    setSexThreshold('60');
    setAgeGroup('');
    setLanguage('');
    setEthnicityFilter('');
    setMinAvgPostRate('');
    setMaxAvgPostRate('');
    setAvailableFrom('');
    setAvailableTo('');
  };

  const pushRecent = (q: string) => {
    const t = q.trim();
    if (t.length < 2) return;
    const next = [t, ...recentSearches.filter((x) => x !== t)].slice(0, 8);
    setRecentSearches(next);
    try {
      if (agencyOrgId) localStorage.setItem(RECENT_KEY(agencyOrgId), JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const saveCurrentSearch = async () => {
    const org = organizationId || agencyOrgId;
    if (!savedSearchName.trim() || !org) return;
    try {
      await api.post('/saved-searches', {
        organizationId: org,
        name: savedSearchName.trim(),
        queryJson: {
          searchTerm,
          selectedTags,
          selectedPlatforms,
          minFollowers,
          maxFollowers,
          minEngagement,
          majorityRace,
          sex,
          sexThreshold,
          ageGroup,
          sortBy,
          language,
          ethnicity: ethnicityFilter,
          minAvgPostRate,
          maxAvgPostRate,
          availableFrom,
          availableTo,
        },
      });
      setSavedSearchName('');
      alert('Search saved');
    } catch (e) {
      console.error(e);
      alert('Could not save search');
    }
  };

  const activeFilterCount = [
    searchTerm,
    selectedTags.length > 0,
    selectedPlatforms.length > 0,
    minFollowers,
    maxFollowers,
    minEngagement,
    majorityRace,
    sex,
    ageGroup,
    language,
    ethnicityFilter,
    minAvgPostRate,
    maxAvgPostRate,
    availableFrom && availableTo,
  ].filter(Boolean).length;

  const getPlatformIcon = (platform: string) => {
    const icons: any = {
      instagram: '📸',
      tiktok: '🎵',
      youtube: '▶️',
      facebook: '📘',
      twitch: '🎮',
    };
    return icons[platform] || '🌐';
  };

  if (!agencyOrgId?.trim()) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="glass-card w-full max-w-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold tracking-tightest text-glass-text">Cannot search KOLs yet</h2>
          <p className="text-sm text-glass-text-2">
            Manual search needs a <strong>managing agency</strong> on the campaign. If the job is still in{' '}
            <strong>Draft</strong>, publish it first (set status to Open) and ensure an agency is assigned — then reopen
            this tool.
          </p>
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-accent2 text-white text-sm font-medium hover:opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-glass">
          <div>
            <h2 className="text-2xl font-semibold tracking-tightest text-glass-text">Manual KOL Selection</h2>
            <p className="text-sm text-glass-text-2 mt-1">
              {totalCount} KOLs found • {selectedKolIds.size} selected
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showFilters ? 'bg-accent2/15 text-accent2 border border-accent2/30' : 'bg-glass-3 text-glass-text-2 hover:bg-glass-2 border border-glass'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-accent2 text-white text-xs px-2 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              onClick={onClose}
              className="text-glass-text-3 hover:text-glass-text transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="w-80 border-r border-glass p-4 overflow-y-auto bg-glass-2 glass-scroll">
              <div className="space-y-4">
                {/* Clear Filters */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-glass-text">Filters</h3>
                  <button
                    onClick={clearFilters}
                    className="text-xs text-accent2 hover:underline font-medium"
                  >
                    Clear All
                  </button>
                </div>

                {/* Tags (hierarchy: parent includes children in search) */}
                <div>
                  <label className="block text-sm font-medium text-glass-text-2 mb-2">
                    Content Tags
                  </label>
                  <div className="space-y-1 text-xs text-glass-text-3 mb-1">Selecting a parent includes all sub-tags.</div>
                  <div className="space-y-0.5 max-h-64 overflow-y-auto glass-scroll">
                    {rootTags.map((root) => {
                      const render = (t: { id: string; name: string }, depth: number) => {
                        const kids = childTagsOf(t.id);
                        return (
                          <div key={t.id} style={{ paddingLeft: depth * 10 }}>
                            <button
                              type="button"
                              onClick={() => toggleTag(t.id)}
                              className={`w-full text-left px-2 py-1.5 rounded text-xs font-medium transition-colors border ${
                                selectedTags.includes(t.id)
                                  ? 'bg-accent2/15 text-accent2 border-accent2/30'
                                  : 'bg-glass-3 text-glass-text-2 border-glass hover:border-glass-strong'
                              }`}
                            >
                              {t.name}
                            </button>
                            {kids.map((ch) => render(ch, depth + 1))}
                          </div>
                        );
                      };
                      return render(root, 0);
                    })}
                  </div>
                </div>

                {/* Platforms */}
                <div>
                  <label className="block text-sm font-medium text-glass-text-2 mb-2">
                    Platforms
                  </label>
                  <div className="space-y-1.5">
                    {['instagram', 'tiktok', 'youtube', 'facebook', 'twitch'].map((platform) => (
                      <button
                        key={platform}
                        onClick={() => togglePlatform(platform)}
                        className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors text-left flex items-center gap-2 border ${
                          selectedPlatforms.includes(platform)
                            ? 'bg-accent2/15 text-accent2 border-accent2/30'
                            : 'bg-glass-3 text-glass-text-2 border-glass hover:border-glass-strong'
                        }`}
                      >
                        <span>{getPlatformIcon(platform)}</span>
                        <span className="capitalize">{platform}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Followers Range */}
                <div>
                  <label className="block text-sm font-medium text-glass-text-2 mb-2">
                    Followers
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={minFollowers}
                      onChange={(e) => setMinFollowers(e.target.value)}
                      className="px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={maxFollowers}
                      onChange={(e) => setMaxFollowers(e.target.value)}
                      className="px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                    />
                  </div>
                </div>

                {/* Engagement */}
                <div>
                  <label className="block text-sm font-medium text-glass-text-2 mb-2">
                    Min Engagement (%)
                  </label>
                  <input
                    type="number"
                    placeholder="e.g., 5"
                    value={minEngagement}
                    onChange={(e) => setMinEngagement(e.target.value)}
                    className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  />
                </div>

                {/* Demographics */}
                <div className="border-t border-glass pt-4">
                  <h4 className="font-medium text-glass-text mb-3">Demographics</h4>
                  
                  {/* Majority Race */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                      Majority Race
                    </label>
                    <select
                      value={majorityRace}
                      onChange={(e) => setMajorityRace(e.target.value)}
                      className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                    >
                      <option value="">Any</option>
                      <option value="malay">Malay</option>
                      <option value="chinese">Chinese</option>
                      <option value="indian">Indian</option>
                    </select>
                  </div>

                  {/* Gender */}
                  <div className="mb-3 grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                        Gender
                      </label>
                      <select
                        value={sex}
                        onChange={(e) => setSex(e.target.value)}
                        className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                      >
                        <option value="">Any</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                        Threshold (%)
                      </label>
                      <input
                        type="number"
                        value={sexThreshold}
                        onChange={(e) => setSexThreshold(e.target.value)}
                        min="50"
                        max="90"
                        disabled={!sex}
                        className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40 disabled:opacity-50"
                      />
                    </div>
                  </div>

                  {/* Age Group */}
                  <div>
                    <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                      Age Group
                    </label>
                    <select
                      value={ageGroup}
                      onChange={(e) => setAgeGroup(e.target.value)}
                      className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                    >
                      <option value="">Any</option>
                      <option value="age18_24">18-24 years</option>
                      <option value="age25_34">25-34 years</option>
                      <option value="age35_plus">35+ years</option>
                    </select>
                  </div>

                  <div className="border-t border-glass pt-4 mt-4 space-y-3">
                    <h4 className="font-medium text-glass-text">More filters</h4>
                    <div>
                      <label className="block text-xs font-medium text-glass-text-2 mb-1">Language (contains)</label>
                      <input
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                        placeholder="e.g. English"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-glass-text-2 mb-1">KOL ethnicity</label>
                      <input
                        value={ethnicityFilter}
                        onChange={(e) => setEthnicityFilter(e.target.value)}
                        className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                        placeholder="malay, chinese..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-glass-text-2 mb-1">Min post rate (RM)</label>
                        <input
                          type="number"
                          value={minAvgPostRate}
                          onChange={(e) => setMinAvgPostRate(e.target.value)}
                          className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-glass-text-2 mb-1">Max post rate (RM)</label>
                        <input
                          type="number"
                          value={maxAvgPostRate}
                          onChange={(e) => setMaxAvgPostRate(e.target.value)}
                          className="w-full px-3 py-2 rounded border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-glass-text-2 mb-1">Available between</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="date" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} className="px-2 py-1 rounded border border-glass bg-glass-2 text-glass-text text-xs focus:outline-none focus:ring-2 focus:ring-accent2/40" />
                        <input type="date" value={availableTo} onChange={(e) => setAvailableTo(e.target.value)} className="px-2 py-1 rounded border border-glass bg-glass-2 text-glass-text text-xs focus:outline-none focus:ring-2 focus:ring-accent2/40" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Search and Controls */}
            <div className="p-4 border-b border-glass space-y-3">
              <div className="flex items-center gap-3">
                <div ref={searchBoxRef} className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-text-3" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setSuggestionsOpen(e.target.value.trim().length >= 2);
                    }}
                    onFocus={() => {
                      if (suggestions.length > 0 && searchTerm.trim().length >= 2) {
                        setSuggestionsOpen(true);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setSuggestionsOpen(false);
                        pushRecent(searchTerm);
                      }
                      if (e.key === 'Escape') {
                        setSuggestionsOpen(false);
                      }
                    }}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  />
                  {suggestionsOpen && suggestions.length > 0 && searchTerm.trim().length >= 2 && (
                    <div className="absolute z-10 mt-1 w-full glass-card border border-glass shadow-lg max-h-48 overflow-y-auto text-sm">
                      {suggestions.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-glass-2 text-glass-text"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSearchTerm(s.name);
                            setSuggestions([]);
                            setSuggestionsOpen(false);
                            pushRecent(s.name);
                          }}
                        >
                          {s.name}
                          {s.email && <span className="text-glass-text-3 ml-2">{s.email}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                >
                  <option value="matchScore">Best Match</option>
                  <option value="followers">Most Followers</option>
                  <option value="engagement">Highest Engagement</option>
                  <option value="name">Name (A-Z)</option>
                </select>
                <div className="flex bg-glass-3 rounded-lg p-1 border border-glass">
                  <button
                    onClick={() => setViewMode('card')}
                    className={`p-2 rounded ${viewMode === 'card' ? 'bg-accent2/15 text-accent2' : 'text-glass-text-2'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-accent2/15 text-accent2' : 'text-glass-text-2'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {selectedKolIds.size === 0 ? (
                    <button
                      onClick={selectAll}
                      className="text-sm text-accent2 hover:underline font-medium"
                    >
                      Select all on this page ({kols.length})
                    </button>
                  ) : (
                    <button
                      onClick={clearSelection}
                      className="text-sm text-rose-500 dark:text-rose-300 hover:underline font-medium"
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {recentSearches.length > 0 && (
                    <span className="text-glass-text-3">Recent:</span>
                  )}
                  {recentSearches.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="px-2 py-0.5 bg-glass-3 border border-glass rounded text-glass-text-2 hover:bg-glass-2"
                      onClick={() => setSearchTerm(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={savedSearchName}
                    onChange={(e) => setSavedSearchName(e.target.value)}
                    placeholder="Save as..."
                    className="px-2 py-1 rounded border border-glass bg-glass-2 text-glass-text text-xs w-36 focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  />
                  <button type="button" className="text-xs text-accent2 font-medium hover:underline" onClick={saveCurrentSearch}>
                    Save search
                  </button>
                </div>
              </div>

              {Object.keys(tagFacet).length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs border-t border-glass pt-2">
                  <span className="text-glass-text-3">In results (root tag):</span>
                  {Object.entries(tagFacet)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 12)
                    .map(([rootId, c]) => (
                      <button
                        key={rootId}
                        type="button"
                        className={`px-2 py-0.5 rounded border ${
                          selectedTags.includes(rootId) ? 'bg-accent2/20 text-accent2 border-accent2/30' : 'bg-glass-3 text-glass-text-2 border-glass hover:bg-glass-2'
                        }`}
                        onClick={() => toggleTag(rootId)}
                      >
                        {tagById.get(rootId)?.name || rootId.slice(0, 6)} ({c})
                      </button>
                    ))}
                </div>
              )}
            </div>

            {totalCount > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-glass bg-glass-2/40 text-sm text-glass-text-2">
                <span>
                  Page {page} of {totalPages}
                  <span className="text-glass-text-3"> · {totalCount} total</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-glass-3"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-glass-3"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* KOL List */}
            <div className="flex-1 overflow-y-auto glass-scroll p-4">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-accent2 border-t-transparent"></div>
                </div>
              ) : kols.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-glass-text-3">
                  <Users className="w-16 h-16 mb-4 text-glass-text-3 opacity-50" />
                  <p className="text-lg font-medium text-glass-text">No KOLs found</p>
                  <p className="text-sm">Try adjusting your filters</p>
                </div>
              ) : viewMode === 'card' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {kols.map((kol) => (
                    <div
                      key={kol.id}
                      onClick={() => toggleKOL(kol.id)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedKolIds.has(kol.id)
                          ? 'border-accent2 bg-accent2/10'
                          : 'border-glass hover:border-glass-strong bg-glass-2'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-glass-text">{kol.name}</h3>
                          <p className="text-sm text-glass-text-3">{kol.email}</p>
                        </div>
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          selectedKolIds.has(kol.id)
                            ? 'bg-accent2 border-accent2'
                            : 'border-glass'
                        }`}>
                          {selectedKolIds.has(kol.id) && <Check className="w-4 h-4 text-white" />}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {kol.tags.slice(0, 3).map((t, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-glass-3 text-glass-text-2 text-xs rounded border border-glass"
                          >
                            {t.tag.name}
                          </span>
                        ))}
                        {kol.tags.length > 3 && (
                          <span className="text-xs text-glass-text-3">+{kol.tags.length - 3}</span>
                        )}
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-glass-text-2 flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            Followers
                          </span>
                          <span className="font-medium text-glass-text">{kol.totalFollowers.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-glass-text-2 flex items-center gap-1">
                            <TrendingUp className="w-4 h-4" />
                            Engagement
                          </span>
                          <span className="font-medium text-glass-text">{kol.avgEngagement}%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-glass-text-2 flex items-center gap-1">
                            <Award className="w-4 h-4" />
                            Match Score
                          </span>
                          <span className="font-medium text-accent2">{kol.matchScore}/100</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-glass-3 sticky top-0 border-b border-glass">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-2 uppercase tracking-[0.05em]">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-2 uppercase tracking-[0.05em]">Tags</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-glass-text-2 uppercase tracking-[0.05em]">Followers</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-glass-text-2 uppercase tracking-[0.05em]">Engagement</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-glass-text-2 uppercase tracking-[0.05em]">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-glass">
                    {kols.map((kol) => (
                      <tr
                        key={kol.id}
                        onClick={() => toggleKOL(kol.id)}
                        className={`cursor-pointer transition-colors ${
                          selectedKolIds.has(kol.id) ? 'bg-accent2/10' : 'hover:bg-glass-2'
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selectedKolIds.has(kol.id)
                              ? 'bg-accent2 border-accent2'
                              : 'border-glass'
                          }`}>
                            {selectedKolIds.has(kol.id) && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-glass-text">{kol.name}</p>
                            <p className="text-sm text-glass-text-3">{kol.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {kol.tags.slice(0, 2).map((t, i) => (
                              <span key={i} className="px-2 py-0.5 bg-glass-3 text-glass-text-2 text-xs rounded border border-glass">
                                {t.tag.name}
                              </span>
                            ))}
                            {kol.tags.length > 2 && (
                              <span className="text-xs text-glass-text-3">+{kol.tags.length - 2}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-glass-text">{kol.totalFollowers.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium text-glass-text">{kol.avgEngagement}%</td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-medium text-accent2">{kol.matchScore}/100</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-glass bg-glass-2">
          <div className="text-sm text-glass-text-2">
            {selectedKolIds.size} KOLs selected (max {BROADCAST_INVITE_BATCH_MAX} per send)
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-glass-text-2 hover:bg-glass-3 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBroadcast}
              disabled={
                selectedKolIds.size === 0 || selectedKolIds.size > BROADCAST_INVITE_BATCH_MAX
              }
              className="btn-accent px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span>Broadcast to {selectedKolIds.size} KOL{selectedKolIds.size !== 1 ? 's' : ''}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

